// File: src/components/SmsConsentPrompt.jsx

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { LuX } from 'react-icons/lu';

import SmsPhoneIllustration from
  '../assets/Illustrations/Flip-Phone-1--Streamline-Milano (1).png';

function getAccessToken() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const storedAuth = JSON.parse(
      localStorage.getItem('tribe_auth') || '{}'
    );

    return (
      storedAuth?.token ||
      localStorage.getItem('accessToken') ||
      ''
    );
  } catch {
    return (
      localStorage.getItem('accessToken') || ''
    );
  }
}

export default function SmsConsentPrompt({
  userId,
}) {
  const [open, setOpen] =
    useState(false);

  const [checking, setChecking] =
    useState(false);

  const [
    pendingAction,
    setPendingAction,
  ] = useState(null);

  const [
    errorMessage,
    setErrorMessage,
  ] = useState('');

  const overlayLockRef = useRef({
    didLock: false,
    y: 0,
    prevTheme: '',
  });

  const loading =
    pendingAction !== null;

  /*
   * The backend owns the eligibility decision.
   */
  useEffect(() => {
    let active = true;

    async function checkEligibility() {
      const accessToken =
        getAccessToken();

      if (!userId || !accessToken) {
        if (active) {
          setOpen(false);
        }

        return;
      }

      setChecking(true);

      try {
        const { data } =
          await axios.get(
            `${process.env.REACT_APP_API_URL}/users/me/sms-consent`,
            {
              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
              },
            }
          );

        if (!active) {
          return;
        }

        setOpen(
          data?.shouldPrompt === true
        );
      } catch (error) {
        console.warn(
          '[SmsConsentPrompt] eligibility check failed:',
          error?.response?.data ||
            error.message
        );

        if (active) {
          setOpen(false);
        }
      } finally {
        if (active) {
          setChecking(false);
        }
      }
    }

    checkEligibility();

    return () => {
      active = false;
    };
  }, [userId]);

  /*
   * Freeze the background at its current scroll position
   * and match the mobile notch/status bar to the overlay.
   */
  useEffect(() => {
    if (
      !open ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const meta = document.querySelector(
      'meta[name="theme-color"]'
    );

    const alreadyLocked =
      document.body.classList.contains(
        'overlay-active'
      ) ||
      document.body.style.position ===
        'fixed';

    overlayLockRef.current.didLock =
      !alreadyLocked;

    overlayLockRef.current.prevTheme =
      meta?.content || '#ffffff';

    if (meta) {
      meta.content = '#888888';
    }

    if (!alreadyLocked) {
      const y =
        window.scrollY ||
        window.pageYOffset ||
        0;

      overlayLockRef.current.y = y;

      document.body.style.top =
        `-${y}px`;

      document.body.classList.add(
        'overlay-active'
      );
    }

    return () => {
      if (meta) {
        meta.content =
          overlayLockRef.current
            .prevTheme || '#ffffff';
      }

      if (
        overlayLockRef.current.didLock
      ) {
        const y =
          overlayLockRef.current.y || 0;

        document.body.classList.remove(
          'overlay-active'
        );

        document.body.style.top = '';

        window.scrollTo(0, y);
      }

      overlayLockRef.current.didLock =
        false;
    };
  }, [open]);

  const dismissPrompt =
    useCallback(async () => {
      if (loading) {
        return;
      }

      const accessToken =
        getAccessToken();

      if (!accessToken) {
        setOpen(false);
        return;
      }

      setPendingAction('dismiss');
      setErrorMessage('');

      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/me/sms-consent/dismiss`,
          {},
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        );

        setOpen(false);
      } catch (error) {
        console.error(
          '[SmsConsentPrompt] dismissal failed:',
          error?.response?.data ||
            error.message
        );

        setErrorMessage(
          error?.response?.data?.message ||
            'Unable to save your choice. Please try again.'
        );
      } finally {
        setPendingAction(null);
      }
    }, [loading]);

  const enableTextUpdates =
    async () => {
      if (loading || !userId) {
        return;
      }

      const accessToken =
        getAccessToken();

      if (!accessToken) {
        setErrorMessage(
          'Please log in again to enable text updates.'
        );

        return;
      }

      setPendingAction('enable');
      setErrorMessage('');

      try {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/users/${userId}/sms-preference`,
          {
            enabled: true,
          },
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        );

        setOpen(false);
      } catch (error) {
        console.error(
          '[SmsConsentPrompt] enable failed:',
          error?.response?.data ||
            error.message
        );

        setErrorMessage(
          error?.response?.data?.message ||
            'Unable to enable text updates. Please try again.'
        );
      } finally {
        setPendingAction(null);
      }
    };

  if (
    checking ||
    !open ||
    typeof document === 'undefined'
  ) {
    return null;
  }

  return createPortal(
    <>
      <div
        className="smsConsentBackdrop"
        role="presentation"
      >
        <section
          className="smsConsentCard"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sms-consent-title"
          aria-describedby="sms-consent-description"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <header className="smsConsentHeader">
            <div className="smsConsentHeaderTitle">
              Quick heads-up
            </div>

            <button
              type="button"
              className="smsConsentClose"
              onClick={dismissPrompt}
              disabled={loading}
              aria-label="Not now"
            >
              <LuX aria-hidden="true" />
            </button>
          </header>

          <div className="smsConsentBody">
            <div
              className="smsConsentIllustration"
              aria-hidden="true"
            >
              <img
                src={SmsPhoneIllustration}
                alt=""
                className="smsConsentPhoneImage"
              />
            </div>

            <div className="smsConsentContent">
              <h2
                id="sms-consent-title"
                className="smsConsentTitle"
              >
                Enable Text Notifications
              </h2>

              <p
                id="sms-consent-description"
                className="smsConsentDescription"
              >
                We use text messages
                to coordinate your
                Tribe experience—
                including personalized
                recommendations,
                reservation reminders,
                and important updates
                for gatherings you
                reserve.
              </p>
            </div>

            {errorMessage && (
              <p
                className="smsConsentError"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <button
              type="button"
              className="smsConsentPrimary"
              onClick={enableTextUpdates}
              disabled={loading}
            >
              {pendingAction ===
              'enable'
                ? 'Saving...'
                : 'Keep me notified'}
            </button>

            <button
              type="button"
              className="smsConsentSecondary"
              onClick={dismissPrompt}
              disabled={loading}
            >
              {pendingAction ===
              'dismiss'
                ? 'Saving...'
                : 'Not now'}
            </button>

            <p className="smsConsentDisclosure">
              By enabling text
              updates, you agree to
              receive recurring SMS
              messages from Tribe,
              including gathering
              recommendations,
              reservation reminders,
              and important gathering
              updates. Message
              frequency varies.
              Message and data rates
              may apply. Reply STOP
              to unsubscribe.{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
              >
                Terms
              </a>
              {' · '}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy
              </a>
            </p>
          </div>
        </section>
      </div>

      <style>
        {`
          .smsConsentBackdrop {
            position: fixed;
            inset: 0;
            z-index: 2147483000;
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            padding: 20px;
            background: rgba(8, 8, 8, 0.48);
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
            font-family:
              Inter,
              ui-sans-serif,
              system-ui,
              -apple-system,
              BlinkMacSystemFont,
              "Segoe UI",
              sans-serif;
          }

          .smsConsentCard {
            position: relative;
            width: 100%;
            max-width: 390px;
            box-sizing: border-box;
            overflow: hidden;
            border: 1px solid rgba(17, 17, 17, 0.10);
            border-radius: 12px;
            background: #ffffff;
            color: #111111;
            box-shadow:
              0 24px 80px rgba(0, 0, 0, 0.22),
              0 4px 18px rgba(0, 0, 0, 0.08);
            text-align: center;
            animation:
              smsConsentEnter
              180ms ease-out;
          }

          .smsConsentHeader {
            height: 48px;
            padding: 0 14px;
            box-sizing: border-box;
            border-bottom: 1px solid #eef0f2;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .smsConsentHeaderTitle {
            color: #0b0b0b;
            font-size: 14px;
            line-height: 1;
            font-weight: 800;
            letter-spacing: -0.01em;
            text-align: left;
          }

          .smsConsentClose {
            width: 32px;
            height: 32px;
            padding: 0;
            border: 0;
            border-radius: 8px;
            background: transparent;
            color: #111111;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 19px;
            line-height: 0;
            -webkit-tap-highlight-color: transparent;
          }

          .smsConsentClose:disabled {
            cursor: default;
            opacity: 0.55;
          }

          .smsConsentBody {
            padding: 20px 24px 22px;
          }

          .smsConsentIllustration {
            height: 152px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 10px;
            margin-bottom: 12px;
          }

          .smsConsentPhoneImage {
            display: block;
            width: 100%;
            max-width: 170px;
            max-height: 152px;
            object-fit: contain;
            transform: rotate(-7deg);
          }

          .smsConsentContent {
            text-align: center;
          }

          .smsConsentTitle {
            margin: 0;
            color: #111111;
            font-size: 22px;
            line-height: 1.12;
            font-weight: 900;
            letter-spacing: -0.045em;
          }

          .smsConsentDescription {
            max-width: 320px;
            margin: 7px auto 0;
            color: #4b5563;
            font-size: 14px;
            line-height: 1.45;
            font-weight: 500;
            letter-spacing: -0.015em;
          }

          .smsConsentError {
            margin: 14px 0 0;
            padding: 10px 12px;
            border: 1px solid #fecaca;
            border-radius: 10px;
            background: #fff5f5;
            color: #b42318;
            font-size: 12px;
            line-height: 1.4;
            font-weight: 650;
            text-align: left;
          }

          .smsConsentPrimary,
          .smsConsentSecondary {
            width: 100%;
            box-sizing: border-box;
            font-family: inherit;
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
          }

          .smsConsentPrimary {
            height: 44px;
            margin-top: 20px;
            padding: 0 14px;
            border: 1px solid #e7bb1a;
            border-radius: 10px;
            background: #ffd84d;
            color: #0b0b0b;
            font-size: 13px;
            font-weight: 800;
          }

          .smsConsentSecondary {
            height: 40px;
            margin-top: 5px;
            padding: 0 14px;
            border: 0;
            border-radius: 10px;
            background: transparent;
            color: #4b5563;
            font-size: 13px;
            font-weight: 750;
          }

          .smsConsentPrimary:disabled,
          .smsConsentSecondary:disabled {
            cursor: default;
            opacity: 0.58;
          }

          .smsConsentDisclosure {
            margin: 14px auto 0;
            color: #858585;
            font-size: 10.5px;
            line-height: 1.48;
            font-weight: 500;
            text-align: left;
          }

          .smsConsentDisclosure a {
            color: #4f4f4f;
            font-weight: 750;
            text-decoration: underline;
            text-underline-offset: 2px;
          }

          @keyframes smsConsentEnter {
            from {
              opacity: 0;
              transform:
                translateY(12px)
                scale(0.985);
            }

            to {
              opacity: 1;
              transform:
                translateY(0)
                scale(1);
            }
          }

          @media (max-width: 520px) {
            .smsConsentBackdrop {
              align-items: flex-end;
              padding:
                10px
                10px
                calc(
                  10px +
                  env(
                    safe-area-inset-bottom,
                    0px
                  )
                );
            }

            .smsConsentCard {
              max-width: none;
              border-radius: 12px;
            }

            .smsConsentBody {
              padding:
                20px
                24px
                22px;
            }
          }

          @media (
            prefers-reduced-motion:
            reduce
          ) {
            .smsConsentCard {
              animation: none;
            }
          }
        `}
      </style>
    </>,
    document.body
  );
}